import type {RunSetupArgs} from '../types/session'
import {isCompletionChunk} from '../types/session'
import type {CustomAgentGroupLogEntry} from './group-agent-logs'
import {isCustomAgentSessionGroup} from './group-agent-logs'

/**
 * Extracts MCP tool name to display name mappings from log entries that contain
 * run_setup tool calls with mcp_setup details.
 *
 * @param logEntries - Array of preprocessed log entries to search through
 * @returns A record mapping tool names to their display names
 */
export function extractMcpToolNameMappings(logEntries: CustomAgentGroupLogEntry[]): Record<string, string> {
  const mappings: Record<string, string> = {}

  for (const logEntry of logEntries) {
    if (isCustomAgentSessionGroup(logEntry) || !isCompletionChunk(logEntry)) {
      continue
    }

    for (const choice of logEntry.choices) {
      const toolCalls = choice.delta.tool_calls ?? []
      for (const toolCall of toolCalls) {
        const toolCallFunction = toolCall?.function
        if (
          !toolCallFunction ||
          toolCallFunction.name !== 'run_setup' ||
          typeof toolCallFunction.arguments !== 'string'
        ) {
          continue
        }

        try {
          // Defensively replace newlines that may appear in JSON to avoid possible parsing errors
          // This may not be needed if the backend is already handling this correctly, but better safe than sorry
          const parsedArgs = JSON.parse(toolCallFunction.arguments.replace(/\n/g, '\\n')) as RunSetupArgs
          if (
            parsedArgs.details?.type === 'mcp_setup' &&
            typeof parsedArgs.details.toolNamesToDisplayNames === 'object'
          ) {
            Object.assign(mappings, parsedArgs.details.toolNamesToDisplayNames ?? {})
          }
        } catch {
          // Ignore parsing errors
        }
      }
    }
  }

  return mappings
}
